<script>
import { defineAsyncComponent, ref, computed, watch } from 'vue';

import NextSidebar from 'next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { useWindowSize } from '@vueuse/core';
import { useStore } from 'dashboard/composables/store';
import { useInactivity } from 'dashboard/composables/useInactivity';
import { useAlert } from 'dashboard/composables';

import lumeratecConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('./commands/commandbar.vue')
);

const FloatingCallWidget = defineAsyncComponent(
  () => import('dashboard/components-next/call/FloatingCallWidget.vue')
);

import MobileSidebarLauncher from 'dashboard/components-next/sidebar/MobileSidebarLauncher.vue';
import { useCallsStore } from 'dashboard/stores/calls';
import KeepAliveModal from 'dashboard/components/widgets/modal/KeepAliveModal.vue';

export default {
  components: {
    NextSidebar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    UpgradePage,
    FloatingCallWidget,
    MobileSidebarLauncher,
    KeepAliveModal,
  },
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId, currentAccount } = useAccount();
    const { width: windowWidth } = useWindowSize();
    const callsStore = useCallsStore();
    const store = useStore();

    const showKeepAliveModal = ref(false);
    const keepAliveAction = ref('offline');

    // Inactivity / Keep-alive logic
    const idleTimeout = computed(() => {
      const settings = currentAccount.value?.settings || {};
      if (
        settings.agent_auto_offline_enabled &&
        settings.agent_auto_offline_minutes
      ) {
        return settings.agent_auto_offline_minutes * 60 * 1000;
      }
      return 0;
    });

    const { isIdle, resetTimer } = useInactivity(idleTimeout);

    watch(isIdle, idleValue => {
      const settings = currentAccount.value?.settings || {};
      if (idleValue && settings.agent_auto_offline_enabled) {
        if (callsStore.hasActiveCall || callsStore.hasIncomingCall) {
          resetTimer();
          return;
        }
        keepAliveAction.value = settings.agent_auto_offline_action || 'offline';
        showKeepAliveModal.value = true;
      } else {
        showKeepAliveModal.value = false;
      }
    });

    const handleKeepAliveContinue = () => {
      showKeepAliveModal.value = false;
      resetTimer();
    };

    const handleKeepAliveExpire = async () => {
      showKeepAliveModal.value = false;
      if (callsStore.hasActiveCall || callsStore.hasIncomingCall) {
        resetTimer();
        return;
      }

      const action = keepAliveAction.value;
      try {
        if (action === 'logout') {
          const { default: Auth } = await import('dashboard/api/auth');
          await Auth.logout();
          return;
        }

        const currentUser = store.getters.getCurrentUser;
        if (currentUser && currentUser.availability_status !== 'offline') {
          await store.dispatch('auth/updateAvailability', {
            availability: 'offline',
            account_id: currentAccount.value.id,
          });
        }
      } catch (error) {
        resetTimer();
        useAlert(
          'Não foi possível aplicar a ação de inatividade. Tente novamente.'
        );
      }
    };

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
      windowWidth,
      hasActiveCall: computed(() => callsStore.hasActiveCall),
      hasIncomingCall: computed(() => callsStore.hasIncomingCall),
      showKeepAliveModal,
      keepAliveAction,
      handleKeepAliveContinue,
      handleKeepAliveExpire,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isMobileSidebarOpen: false,
    };
  },
  computed: {
    isSmallScreen() {
      return this.windowWidth < lumeratecConstants.SMALL_SCREEN_BREAKPOINT;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    isCalendarPage() {
      return ['calendar_index', 'calendar_settings'].includes(this.$route.name);
    },
    previouslyUsedDisplayType() {
      const {
        previously_used_conversation_display_type: conversationDisplayType,
      } = this.uiSettings;
      return conversationDisplayType;
    },
  },
  watch: {
    isSmallScreen: {
      handler(isSmallScreen) {
        const { LAYOUT_TYPES } = lumeratecConstants;
        if (isSmallScreen) {
          this.updateUISettings({
            conversation_display_type: LAYOUT_TYPES.EXPANDED,
          });
        } else {
          this.updateUISettings({
            conversation_display_type: this.previouslyUsedDisplayType,
          });
        }
      },
      immediate: true,
    },
  },
  mounted() {
    window.addEventListener(
      'open-keyboard-shortcuts',
      this.toggleKeyShortcutModal
    );
  },
  unmounted() {
    window.removeEventListener(
      'open-keyboard-shortcuts',
      this.toggleKeyShortcutModal
    );
  },
  methods: {
    toggleMobileSidebar() {
      this.isMobileSidebarOpen = !this.isMobileSidebarOpen;
    },
    closeMobileSidebar() {
      this.isMobileSidebarOpen = false;
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
  },
};
</script>

<template>
  <div class="flex flex-grow overflow-hidden text-n-slate-12">
    <NextSidebar
      :is-mobile-sidebar-open="isMobileSidebarOpen"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
      @close-mobile-sidebar="closeMobileSidebar"
    />

    <main
      id="main-content"
      tabindex="-1"
      class="flex h-full min-h-0 w-full flex-1 px-0 bg-n-surface-1"
      :class="
        isCalendarPage ? 'overflow-x-hidden overflow-y-auto' : 'overflow-hidden'
      "
    >
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      >
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
      </UpgradePage>
      <template v-if="!showUpgradePage">
        <router-view />
        <CommandBar />
        <MobileSidebarLauncher
          :is-mobile-sidebar-open="isMobileSidebarOpen"
          @toggle="toggleMobileSidebar"
        />
        <FloatingCallWidget v-if="hasActiveCall || hasIncomingCall" />
      </template>
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
      <KeepAliveModal
        v-if="showKeepAliveModal"
        v-model:show="showKeepAliveModal"
        :action="keepAliveAction"
        @continue="handleKeepAliveContinue"
        @expire="handleKeepAliveExpire"
      />
    </main>
  </div>
</template>
